import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PortalLayout from '@/components/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import {
  Buildings,
  Plus,
  MagnifyingGlass,
  ArrowRight,
  Trash,
  UsersThree,
  ArrowsClockwise,
} from '@phosphor-icons/react';

const blankChild = {
  account_type: 'business',
  company_name: '',
  company_email: '',
  company_phone: '',
  physical_address: '',
  residential_address: '',
  id_number: '',
  vat_number: '',
  parent_company_id: '',
};

const PortalSubAccounts = () => {
  const [loading, setLoading] = useState(true);
  const [parents, setParents] = useState([]);          // companies the client owns directly
  const [children, setChildren] = useState([]);        // sub-accounts under those
  const [search, setSearch] = useState('');
  const [parentFilter, setParentFilter] = useState('all');
  const [openCreate, setOpenCreate] = useState(false);
  const [newChild, setNewChild] = useState(blankChild);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dashRes, childRes] = await Promise.all([
        api.get('/dashboard/client'),
        api.get('/portal/children'),
      ]);
      const allCompanies = dashRes.data?.companies || [];
      // Parents = companies that have no parent themselves (i.e. the user's own profile/companies)
      const owned = allCompanies.filter(c => !c.parent_company_id);
      setParents(owned);
      setChildren(childRes.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load sub-accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const filteredChildren = useMemo(() => {
    let list = children;
    if (parentFilter !== 'all') list = list.filter(c => c.parent_company_id === parentFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.company_name?.toLowerCase().includes(q) ||
        c.company_email?.toLowerCase().includes(q) ||
        c.id_number?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [children, parentFilter, search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newChild.parent_company_id) { toast.error('Please pick a parent profile'); return; }
    if (!newChild.company_name.trim()) { toast.error('Name is required'); return; }
    setCreating(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(newChild).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      );
      await api.post('/portal/children', payload);
      toast.success('Sub-account added');
      setOpenCreate(false);
      setNewChild({ ...blankChild, parent_company_id: newChild.parent_company_id });
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/portal/children/${deleteTarget.company_id}`);
      toast.success(`${deleteTarget.company_name} removed`);
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const defaultParentId = parents[0]?.company_id || '';

  return (
    <PortalLayout title="My Clients (Sub-Accounts)">
      <div className="space-y-6 fade-in">
        {/* Intro */}
        <Card className="border-l-4 border-l-[#4CAF50]">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <UsersThree className="w-6 h-6 text-[#4CAF50] mt-0.5" weight="fill" />
              <div className="flex-1">
                <h3 className="font-semibold text-[#0A192F]">Manage clients on your profile</h3>
                <p className="text-sm text-gray-600 mt-1">
                  If you act as a middle-man and handle municipal accounts for other businesses or individuals,
                  add them here as sub-accounts. You can then submit service requests, upload documents, and
                  track cases on their behalf — all from your single login.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters + Add */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search by name, email, ID…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 rounded-none"
                  data-testid="search-children-input"
                />
              </div>

              {parents.length > 1 && (
                <Select value={parentFilter} onValueChange={setParentFilter}>
                  <SelectTrigger className="w-56 rounded-none" data-testid="parent-filter">
                    <SelectValue placeholder="All profiles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All profiles</SelectItem>
                    {parents.map(p => (
                      <SelectItem key={p.company_id} value={p.company_id}>{p.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Button
                variant="outline"
                onClick={fetchAll}
                className="rounded-none"
                data-testid="refresh-children-btn"
                aria-label="Refresh"
              >
                <ArrowsClockwise className="w-4 h-4" />
              </Button>

              <Dialog open={openCreate} onOpenChange={(o) => { setOpenCreate(o); if (o) setNewChild({ ...blankChild, parent_company_id: defaultParentId }); }}>
                <DialogTrigger asChild>
                  <Button className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none" data-testid="add-child-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Sub-Account
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[520px]">
                  <DialogHeader>
                    <DialogTitle>Add a Client / Sub-Account</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4 mt-2">
                    <div>
                      <Label className="text-sm font-semibold">Account Type *</Label>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        {['business', 'personal'].map(t => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setNewChild({ ...newChild, account_type: t })}
                            className={`p-3 border-2 text-sm font-medium transition-colors ${
                              newChild.account_type === t
                                ? 'border-[#4CAF50] bg-[#4CAF50]/5 text-[#4CAF50]'
                                : 'border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                            data-testid={`child-account-type-${t}`}
                          >
                            {t === 'business' ? 'Business' : 'Personal'}
                          </button>
                        ))}
                      </div>
                    </div>

                    {parents.length > 1 && (
                      <div>
                        <Label className="text-sm font-semibold">Linked to my profile *</Label>
                        <Select value={newChild.parent_company_id} onValueChange={(v) => setNewChild({ ...newChild, parent_company_id: v })}>
                          <SelectTrigger className="rounded-none mt-1" data-testid="child-parent-select">
                            <SelectValue placeholder="Select parent profile" />
                          </SelectTrigger>
                          <SelectContent>
                            {parents.map(p => (
                              <SelectItem key={p.company_id} value={p.company_id}>{p.company_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div>
                      <Label className="text-sm font-semibold">
                        {newChild.account_type === 'business' ? 'Company Name *' : 'Full Name *'}
                      </Label>
                      <Input
                        value={newChild.company_name}
                        onChange={(e) => setNewChild({ ...newChild, company_name: e.target.value })}
                        required
                        className="rounded-none mt-1"
                        data-testid="child-name-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm font-semibold">Email</Label>
                        <Input
                          type="email"
                          value={newChild.company_email}
                          onChange={(e) => setNewChild({ ...newChild, company_email: e.target.value })}
                          className="rounded-none mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold">Phone</Label>
                        <Input
                          value={newChild.company_phone}
                          onChange={(e) => setNewChild({ ...newChild, company_phone: e.target.value })}
                          className="rounded-none mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-semibold">
                        {newChild.account_type === 'personal' ? 'Residential Address' : 'Physical Address'}
                      </Label>
                      <Input
                        value={newChild.account_type === 'personal' ? newChild.residential_address : newChild.physical_address}
                        onChange={(e) => setNewChild(newChild.account_type === 'personal'
                          ? { ...newChild, residential_address: e.target.value }
                          : { ...newChild, physical_address: e.target.value })}
                        className="rounded-none mt-1"
                      />
                    </div>

                    {newChild.account_type === 'personal' ? (
                      <div>
                        <Label className="text-sm font-semibold">ID Number</Label>
                        <Input
                          value={newChild.id_number}
                          onChange={(e) => setNewChild({ ...newChild, id_number: e.target.value })}
                          className="rounded-none mt-1 font-mono"
                          placeholder="13-digit SA ID"
                        />
                      </div>
                    ) : (
                      <div>
                        <Label className="text-sm font-semibold">VAT Number</Label>
                        <Input
                          value={newChild.vat_number}
                          onChange={(e) => setNewChild({ ...newChild, vat_number: e.target.value })}
                          className="rounded-none mt-1 font-mono"
                        />
                      </div>
                    )}

                    <div className="flex justify-end gap-3 pt-2">
                      <Button type="button" variant="outline" onClick={() => setOpenCreate(false)} className="rounded-none">Cancel</Button>
                      <Button type="submit" disabled={creating} className="bg-[#4CAF50] hover:bg-[#45a049] rounded-none" data-testid="submit-child-btn">
                        {creating ? 'Adding…' : 'Add Sub-Account'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* List */}
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-base text-[#0A192F] flex items-center gap-2">
              <Buildings className="w-5 h-5 text-[#4CAF50]" />
              Sub-Accounts <span className="text-sm text-gray-400 font-normal">({filteredChildren.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {loading ? (
              <div className="flex items-center justify-center h-24">
                <div className="w-6 h-6 border-2 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : filteredChildren.length === 0 ? (
              <div className="text-center py-12 text-sm text-gray-500">
                {children.length === 0
                  ? 'No sub-accounts yet. Click "Add Sub-Account" to register your first client.'
                  : 'No sub-accounts match your search.'}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredChildren.map(c => (
                  <div key={c.company_id} className="relative group border border-gray-200 hover:border-[#4CAF50] transition-colors p-4" data-testid={`child-card-${c.company_id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#4CAF50]/10 flex items-center justify-center shrink-0">
                        <Buildings className="w-5 h-5 text-[#4CAF50]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#0A192F] truncate">{c.company_name}</p>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{c.company_email || c.id_number || '—'}</p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <Badge variant="outline" className="text-xs rounded-none">
                            {c.account_type === 'personal' ? 'Personal' : 'Business'}
                          </Badge>
                          {parents.length > 1 && c.parent_company_name && (
                            <span className="text-xs text-gray-400">↳ {c.parent_company_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-2 border-t pt-3">
                      <Link
                        to={`/portal/service-request`}
                        state={{ preselectCompanyId: c.company_id }}
                        className="text-sm text-[#4CAF50] hover:underline inline-flex items-center gap-1"
                        data-testid={`new-request-for-${c.company_id}`}
                      >
                        New request <ArrowRight className="w-3 h-3" />
                      </Link>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(c)}
                        className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label={`Remove ${c.company_name}`}
                        data-testid={`delete-child-${c.company_id}`}
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleteTarget?.company_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this sub-account along with all of its cases, documents,
              quotations, and invoices. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-child"
            >
              {deleting ? 'Removing…' : 'Remove Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PortalLayout>
  );
};

export default PortalSubAccounts;
